import { Box, Avatar, Typography, CircularProgress } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { ChatMessage } from '../../../IdeationService';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import { formatTimestamp } from '../../../../SimulationXPLR/utils';
import { getFileIcon } from '../../../../../common/utils';
import CustomTooltip from '../../../../../common/components/CustomToolTip/CustomToolTip';

interface UserMessageProps {
  message: ChatMessage;
  onFileClick: (url: string, fileName?: string) => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Turns "john.doe" or "John Doe" → "JD" */
const getInitialsFromUsername = (username: string): string => {
  const parts = username.split(/[.\s]+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/** Title-cases a username string for display in tooltips */
const formatDisplayName = (username: string): string =>
  username
    .split(/[.\s]+/)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(' ');

const formatFileSize = (size: string): string => {
  if (!size) return size;
  const kb = parseInt(size.replace('KB', ''));
  return kb > 1024 ? `${(kb / 1024).toFixed(2)} mb` : `${kb} kb`;
};

// ── Sub-components ─────────────────────────────────────────────────────────────

/** Single file chip shown below the text bubble */
const FileChip = ({
  file,
  onClick,
}: {
  file: any;
  onClick: (url: string, fileName?: string) => void;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      border: '1px solid #E0E0E0',
      borderRadius: '4px',
      bgcolor: '#ffffff',
      px: 1,
      py: 0.5,
      minWidth: 180,
      maxWidth: '308px',
      opacity: file.isUploading || file.isAnalyzing ? 0.6 : 1,
      cursor: 'pointer',
      mr: 1,
      my: 0.5,
    }}
    onClick={() => {
      const url = file?.fileUrl || file?.url || file?.file_url;
      if (url) onClick(url, file?.name || file?.fileName);
    }}
  >
    {/* Icon box */}
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '6px',
        bgcolor: '#f1f3f4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        mr: 1,
      }}
    >
      {file.isUploading || file.isAnalyzing ? (
        <CircularProgress size={14} sx={{ color: '#5f6368' }} />
      ) : (
        getFileIcon(file.name, file.file)
      )}
    </Box>

    {/* Name + size */}
    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            fontSize: '14px',
            color: '#202124',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {file.name || file?.fileName}
        </Typography>
        <img src="/images/arrbtn.svg" alt="arrowbtn" />
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontSize: '12px',
          color: 'rgba(108,117,125,1)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {formatFileSize(file.fileSize)}
      </Typography>
    </Box>
  </Box>
);

/** Single context knowledge chip */
const ContextChip = ({ context }: { context: any }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      border: '1px solid #E0E0E0',
      borderRadius: '12px',
      bgcolor: '#fafafa',
      px: 1,
      py: 0.5,
      minWidth: 180,
      maxWidth: '100%',
      mr: 1,
    }}
  >
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '6px',
        bgcolor: '#f1f3f4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mr: 1,
      }}
    >
      <AttachFileIcon sx={{ fontSize: 18, color: '#5f6368', transform: 'rotate(45deg)' }} />
    </Box>
    <Typography
      variant="body2"
      sx={{
        flex: 1,
        fontWeight: 500,
        fontSize: '14px',
        color: '#202124',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {context?.use_case_title || 'Process Context'}
    </Typography>
  </Box>
);

// ── Main component ─────────────────────────────────────────────────────────────

const UserMessage = ({ message, onFileClick }: UserMessageProps) => {
  const user = useSelector((state: RootState) => state.user);

  // Decide which initials and tooltip name to show
  const initials = message.username
    ? getInitialsFromUsername(message.username)
    : `${user?.first_name?.charAt(0)?.toUpperCase() ?? ''}${user?.last_name?.charAt(0)?.toUpperCase() ?? ''}`;

  const tooltipName = message.username
    ? formatDisplayName(message.username)
    : `${user?.first_name ? user.first_name.charAt(0).toUpperCase() + user.first_name.slice(1).toLowerCase() : ''} ${user?.last_name ? user.last_name.charAt(0).toUpperCase() + user.last_name.slice(1).toLowerCase() : ''}`.trim();

  const files: any[] = Array.isArray(message?.query?.user_file_data)
    ? message.query.user_file_data
    : Array.isArray(message?.files?.data)
      ? message.files.data
      : [];
  const contexts: any[] = message?.context_knowledge?.data || [];

  return (
    <Box
      sx={{
        mb: 2,
        mr: 2,
        display: 'flex',
        gap: 1,
        cursor: 'pointer',
        alignItems: 'flex-start',
      }}
    >
      {/* Avatar with tooltip */}
      <CustomTooltip variant="left" title={tooltipName}>
        <Avatar sx={{ width: 32, height: 32, backgroundColor: '#A3BBFD', fontSize: '14px' }}>
          {initials}
        </Avatar>
      </CustomTooltip>

      <Box sx={{ width: '90%' }}>
        {/* Text bubble */}
        {message?.query?.user_query && (
          <Box
            sx={{
              width: '96%',
              backgroundColor: '#F2F5FF',
              borderRadius: '12px',
              p: 2,
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              overflow: 'hidden',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#000000',
                fontSize: '16px',
                fontWeight: '400',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
            >
              {message.query.user_query}
            </Typography>
          </Box>
        )}

        {/* File + context chips */}
        {(files.length > 0 || contexts.length > 0) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 0.5,
              maxWidth: '100%',
              mt: 1,
            }}
          >
            {files.map((file: any) => (
              <FileChip key={file.id} file={file} onClick={onFileClick} />
            ))}
            {contexts.map((context: any) => (
              <ContextChip key={context} context={context} />
            ))}
          </Box>
        )}

        {/* Timestamp */}
        <Typography
          variant="caption"
          sx={{ color: '#999', fontSize: '11px', mt: 1, display: 'block' }}
        >
          {formatTimestamp(message?.created_at || '')}
        </Typography>
      </Box>
    </Box>
  );
};

export default UserMessage;
